import { type AsyncThunk, createAsyncThunk } from '@reduxjs/toolkit';

import type {
  ActionHandler,
  ActionHandlers,
  AsyncThunkActionDefinition,
  PageReducerState,
} from './types';

function actionPending() {
  return (_state: unknown) => {
    // Don't set isProcessing for sync actions — they're instant and would
    // cause unnecessary renders.
    void _state;
  };
}

function actionSuccessful<TState>(actionHandler: ActionHandler<TState>) {
  return function (state: unknown, action: unknown) {
    const s = state as PageReducerState<TState>;
    let navigateTo: string | null = null;
    const navigateFx = (url: string) => {
      navigateTo = url;
    };

    const a = action as { payload?: unknown[] };
    const params = a?.payload?.[0];

    const changedState = actionHandler(s.data, {
      action,
      navigate: navigateFx,
      params,
    } as unknown as Parameters<ActionHandler<TState>>[1]);
    s.data = changedState;
    if (navigateTo) {
      s.pageState.navigateTo = navigateTo;
    }
  };
}

function actionError<TState>() {
  return (state: unknown) => {
    const s = state as PageReducerState<TState>;
    s.pageState.isProcessing = false;
  };
}

export function createSyncActions<TState>(
  name: string,
  reducerActions: ActionHandlers<TState>,
): {
  asyncActions: Record<string, AsyncThunk<unknown, unknown, object>>;
  asyncThunkActionDefinitions: AsyncThunkActionDefinition[];
} {
  const asyncActions = {} as Record<string, AsyncThunk<unknown, unknown, object>>;
  const asyncThunkActionDefinitions: AsyncThunkActionDefinition[] = [];

  Object.keys(reducerActions || {}).map((key) => {
    const actionHandler = reducerActions[key];

    const actionCreator = createAsyncThunk(`${name}/${key}`, (params: unknown) => {
      // Sync actions are dispatched through useDispatchActions, which always
      // wraps the call as `{ args, extra }`. A bare-thunk dispatch (callsite
      // forgot the envelope) would silently produce `params: undefined` in
      // the handler — fail loudly instead so the misuse is obvious in dev.
      if (!params || typeof params !== 'object' || !('args' in params)) {
        // RTK catches throws inside payload creators and routes them through
        // the rejected lifecycle (`actionError` here, which only resets
        // isProcessing). Without a console.error the misuse is invisible —
        // log first so the dev sees something even if no UI surfaces it.
        const message = `[${name}/${key}] sync action dispatched without { args, extra } envelope — use useDispatchActions instead of dispatching the thunk directly.`;
        console.error(message);
        throw new Error(message);
      }
      const p = params as { args?: unknown[] };
      return Promise.resolve(p.args);
    });
    asyncActions[key] = actionCreator as AsyncThunk<unknown, unknown, object>;

    asyncThunkActionDefinitions.push({
      asyncThunk: actionCreator as AsyncThunk<unknown, unknown, object>,
      pending: actionPending(),
      onSuccess: actionSuccessful(actionHandler),
      onError: actionError<TState>(),
    });
  });

  return { asyncActions, asyncThunkActionDefinitions };
}
